import React from 'react';
import { Text, type TextStyle } from 'react-native';
import { isAndroid } from '../isAndroid';

type TextNode = {
  text: string;
  color?: string;
  fontWeight?: 'normal' | 'bold';
  fontSize?: number;
};

/**
 * HTML 문자열을 파싱하여 TextNode 배열로 변환
 * 중첩된 태그를 올바르게 처리하기 위해 스택 기반 접근 방식 사용
 * @example
 * parseHTMLToTextNodes("<span style='color: #777777;font-size: 16px;'> <b>'≈≈≈'</b>에 대한 검색 결과가 없습니다.</span>")
 */
const parseHTMLToTextNodes = (html: string): TextNode[] => {
  const nodes: TextNode[] = [];
  const styleStack: Array<{
    color?: string;
    fontSize?: number;
    fontWeight: 'normal' | 'bold';
  }> = [{ fontWeight: 'normal' }];

  let currentIndex = 0;

  // HTML 태그를 파싱하는 함수 (span과 b 태그만 지원)
  const parseTag = (
    tag: string,
  ): {
    type: 'span' | 'b' | 'close-span' | 'close-b' | null;
    style?: Record<string, string>;
  } => {
    const tagMatch = tag.match(/<(\/?)(\w+)([^>]*)>/);
    if (!tagMatch) {
      return { type: null };
    }

    const [, isClose, tagName, attributes] = tagMatch;

    if (isClose) {
      if (tagName === 'span' || tagName === 'font')
        return { type: 'close-span' };
      if (tagName === 'b' || tagName === 'strong') return { type: 'close-b' };
      return { type: null };
    }

    if (tagName === 'span') {
      // style 속성 파싱
      const styleMatch = attributes.match(/style=['"]([^'"]+)['"]/);
      const style: Record<string, string> = {};
      if (styleMatch) {
        const styleString = styleMatch[1];
        const stylePairs = styleString.split(';');
        stylePairs.forEach(pair => {
          const [key, value] = pair.split(':').map(s => s.trim());
          if (key && value) {
            style[key] = value;
          }
        });
      }
      return { type: 'span', style };
    }

    if (tagName === 'font') {
      // <font color='#FF5160'> 형식 지원
      const colorMatch = attributes.match(/color=['"]([^'"]+)['"]/);
      const style: Record<string, string> = {};
      if (colorMatch) {
        style.color = colorMatch[1];
      }
      return { type: 'span', style };
    }

    if (tagName === 'b' || tagName === 'strong') {
      return { type: 'b' };
    }

    return { type: null };
  };

  // 스타일에서 color 추출 (hex color를 그대로 반환)
  const extractColor = (style: Record<string, string>): string | undefined => {
    const color = style.color?.trim();
    return color || undefined;
  };

  // 스타일에서 font-size 추출
  const extractFontSize = (
    style: Record<string, string>,
  ): number | undefined => {
    const fontSize = style['font-size']?.trim();
    if (!fontSize) return undefined;

    // 16px 형식에서 숫자만 추출
    const match = fontSize.match(/(\d+)px/);
    if (match) {
      return parseInt(match[1], 10);
    }

    return undefined;
  };

  // HTML 문자열을 순회하면서 파싱
  while (currentIndex < html.length) {
    // 태그 시작 찾기
    const tagStart = html.indexOf('<', currentIndex);
    if (tagStart === -1) {
      // 태그가 없으면 나머지 텍스트 추가
      const remainingText = html.slice(currentIndex);
      if (remainingText) {
        const currentStyle = styleStack[styleStack.length - 1];
        nodes.push({
          text: remainingText,
          color: currentStyle.color,
          fontWeight: currentStyle.fontWeight,
          fontSize: currentStyle.fontSize,
        });
      }
      break;
    }

    // 태그 이전의 텍스트 추가
    if (tagStart > currentIndex) {
      const text = html.slice(currentIndex, tagStart);
      if (text) {
        const currentStyle = styleStack[styleStack.length - 1];
        nodes.push({
          text,
          color: currentStyle.color,
          fontWeight: currentStyle.fontWeight,
          fontSize: currentStyle.fontSize,
        });
      }
    }

    // 태그 끝 찾기
    const tagEnd = html.indexOf('>', tagStart);
    if (tagEnd === -1) break;

    const tag = html.slice(tagStart, tagEnd + 1);
    const parsedTag = parseTag(tag);

    // 지원하지 않는 태그는 무시
    if (parsedTag.type === null) {
      currentIndex = tagEnd + 1;
      continue;
    }

    const currentStyle = styleStack[styleStack.length - 1];
    const newStyle = { ...currentStyle };

    if (parsedTag.type === 'span' && parsedTag.style) {
      // span 태그의 스타일 적용
      const color = extractColor(parsedTag.style);
      const fontSize = extractFontSize(parsedTag.style);
      if (color) newStyle.color = color;
      if (fontSize) newStyle.fontSize = fontSize;
      styleStack.push(newStyle);
    } else if (parsedTag.type === 'b') {
      // bold 태그 시작
      newStyle.fontWeight = 'bold';
      styleStack.push(newStyle);
    } else if (
      parsedTag.type === 'close-span' ||
      parsedTag.type === 'close-b'
    ) {
      // 닫는 태그 처리
      if (styleStack.length > 1) {
        styleStack.pop();
      }
    }

    currentIndex = tagEnd + 1;
  }

  return nodes;
};

/**
 * HTML 문자열을 React Native Text 컴포넌트로 변환
 * @example
 * // 입력
 * renderHTMLToText("<span style='color: #777777;font-size: 16px;'> <b>'≈≈≈'</b>에 대한 검색 결과가 없습니다.</span>")
 *
 * // 결과 (React Native 컴포넌트)
 * <Text>
 *   <Text style={{ color: '#777777', fontSize: 16 }}> </Text>
 *   <Text style={{ color: '#777777', fontSize: 16, fontWeight: 'bold' }}>'≈≈≈'</Text>
 *   <Text style={{ color: '#777777', fontSize: 16 }}>에 대한 검색 결과가 없습니다.</Text>
 * </Text>
 */
export const renderHTMLToText = (
  html: string,
  options?: { style?: TextStyle; numberOfLines?: number },
): React.ReactElement => {
  const nodes = parseHTMLToTextNodes(html);

  if (nodes.length === 0) {
    return (
      <Text style={options?.style} numberOfLines={options?.numberOfLines}>
        {html}
      </Text>
    );
  }

  return (
    <Text style={options?.style} numberOfLines={options?.numberOfLines}>
      {nodes.map((node, index) => {
        const nodeStyle: TextStyle = {};

        if (node.color) {
          nodeStyle.color = node.color;
        }

        const fontWeight = isAndroid
          ? {
              regular: 'pretendard_regular',
              bold: 'pretendard_semi_bold',
            }
          : {
              regular: 'Pretendard-Regular',
              bold: 'Pretendard-SemiBold',
            };

        if (node.fontWeight === 'bold') {
          nodeStyle.fontFamily = fontWeight.bold;
        } else {
          nodeStyle.fontFamily = fontWeight.regular;
        }

        if (node.fontSize) {
          nodeStyle.fontSize = node.fontSize;
        }

        return (
          <Text key={index} style={nodeStyle}>
            {node.text}
          </Text>
        );
      })}
    </Text>
  );
};
